import ConfigurationService from '../Service/ConfigurationService';
import { GeoJsonFileListResponse, PaginatedResponse, RequestConfig, Tree, TreeValidation } from 'tree-wallet-client';
import { request } from '../Helper/Http';
import DBService, { TABLE_TREES } from '../Service/DBService';
import { dataOrNull, floatOrNull, integerOrNull, stringOrNull, toObject } from '../Helper/DataHelper';
import * as TreeService from '../Service/TreeService';
import message from "antd/es/message";
import { getToken } from '../Service/AuthService';

export interface TreeFormData {
  typeId: number;
  userId: number;
  contractId: number;
  lat: number;
  lng: number;
  speed: number;
  accuracy: number;
  number: number;
  photo: any;
}

interface TreeCountResult {
  trees: number;
}

export async function getIndexes() {
  return await DBService.getAllKeys(TABLE_TREES);
}

export async function getTotalTreeCount() {
  let total = 0;
  await DBService.getCursor(TABLE_TREES, (cursor: any) => {
    const item = formatTreeData(cursor.value);
    total += integerOrNull(item.number) || 0;
  });
  return total;
}

export async function getGeoJsons(config: RequestConfig, isPublic?: boolean): Promise<GeoJsonFileListResponse> {
  const { params } = config;
  let url = `${ConfigurationService.urls().treeGeoJsonListUrl}`

  if ((!params || !params.contractId) && !isPublic) {
    throw new Error('missing parameter contractId');
  }

  if (!config.admin && !isPublic) {
    url += `/${params.contractId}`;
  }

  return await request(url, config);
}

export async function getHighlightedCountries(config: RequestConfig = {}): Promise<any> {
  const url = ConfigurationService.urls().highlightedCountriesUrl;
  // Skip authentication for public endpoints
  return await request(url, { ...config, disableAuth: true }) as any;
}

export async function getPublicGeoJsons(config: RequestConfig): Promise<GeoJsonFileListResponse> {
  const url = `${ConfigurationService.urls().treePublicGeoJsonListUrl}`

  return await request(url, config);
}

export async function getPublicCountByCountry(config: RequestConfig): Promise<any> {
  const { params } = config;

  if (!params || !params.country_code) {
    throw new Error('missing parameter country_code');
  }

  const url = `${ConfigurationService.urls().treePublicGeoJsonFileUrl}&countryId=${params.country_code}`
  console.log(url, 'URL OF THE PUBLIC COUNT BY COUNTRY')
  return await request(url, { ...config, disableAuth: true });
}

export async function getPublicTree(config: RequestConfig): Promise<Tree> {
  const { params } = config;

  if (!params || !params.treeId) {
    throw new Error('missing parameter treeId');
  }

  const url = `${ConfigurationService.urls().treePublicUrl}/${params.treeId}`
  return await request(url, config);
}

export async function getTree(config: RequestConfig): Promise<Tree> {
  const { params } = config;

  if (!params || !params.treeId) {
    throw new Error('missing parameter treeId');
  }

  const url = `${ConfigurationService.urls().treeUrl}/${params.treeId}`
  return await request(url, config);
}

export async function getTreeList(config: RequestConfig): Promise<PaginatedResponse<Tree[]>> {
  const url = `${ConfigurationService.urls().treeListUrl}`
  return await request(url, config);
}

export async function createTree(formData: FormData, successCallback?: Function, errorCallback?: Function) {
  let requestError: any = null;

  await request(
    ConfigurationService.urls().treeCreateUrl,
    {
      httpConfig: {
        method: 'POST',
        body: formData,
        multipart: true,
      },
      successCallback: (response: any) => {
        try {
          if (formData.has('number')) {
            TreeService.incrementTrees(formData.get('number'));
          }
        } catch (error) {
          // the tree is stored server side, a counter update must never undo that
          console.error('could not update the local tree counter', { error });
        }
        successCallback && successCallback(response)
      },
      errorCallback: (error: any) => {
        requestError = error;
        errorCallback && errorCallback(error);
      }
    }
  );

  // request() reports failures through the callback and resolves with undefined.
  // The offline queue needs a real rejection, otherwise a tree that was never
  // stored would be dropped from the queue as if it had been synced.
  if (requestError) {
    throw requestError;
  }
}

export async function getTreeCount(config?: RequestConfig): Promise<TreeCountResult> {
  let url = ConfigurationService.urls().treeCountUrl;
  if (config && config.params && config.params.contractId) {
    url = url + '/' + config.params.contractId;
  }

  if (!navigator.onLine) {
    config && config.successCallback && config.successCallback(TreeService.getTreeCount());
    return { trees: TreeService.getTreeCount() };
  }

  return await request(
    url,
    {
      successCallback: (treeCount: any) => {
        if (treeCount && treeCount.trees) {
          TreeService.setTreeCount(treeCount.trees);
        }
        config && config.successCallback && config.successCallback(treeCount)
      },
      errorCallback: (error: any) => {
        console.error('error fetching tree count', error);
        config && config.errorCallback && config.errorCallback(error);
      }
    }
  );
}

export async function getTreesDataFromIdb(nbTrees?: number) {
  const data: any[] = [];
  await DBService.getCursor(TABLE_TREES, (cursor: any) => {
    const item = formatTreeData(cursor.value);
    if (!nbTrees || data.length < nbTrees) {
      data.push({ data: item, id: cursor.key });
    }
  });

  return data;
}

export async function deleteTree(tree: Tree, config?: RequestConfig) {
  const number = tree.numberPlanted;
  const req = await request(
    `${ConfigurationService.urls().treeDeleteUrl}/${tree.id}`,
    {
      ...config,
      successCallback: (response: any) => {
        TreeService.decrementTrees(number);
        config?.successCallback?.(response);
      }
    }
  );
  return req;
}

export async function getOne(id: number, onError?: Function) {
  const data = await DBService.getValue(TABLE_TREES, id, onError);
  return formatTreeData(data);
}

export async function processTrees(callback: Function) {
  const data: any[] = [];
  await DBService.getCursor(TABLE_TREES, (cursor: any) => {
    const item = formatTreeData(cursor.value);
    callback(item)
  });

  return data;
}

export async function moveTrees(treeIds: number[], numberOfTrees: number, contractId: number) {
  const url = `${ConfigurationService.urls().treeMoveUrl}?to=${contractId}&nb=${numberOfTrees}`;
  return await request(url, {
    httpConfig: {
      method: 'POST',
      body: JSON.stringify({ treeIds })
    }
  });
}

export async function getTreeValidationHistory(config: RequestConfig): Promise<PaginatedResponse<TreeValidation[]>> {
  const { params } = config;

  if (!params || !params.treeId) {
    throw new Error('missing parameter treeId');
  }

  const url = `${ConfigurationService.urls().treeValidationHistoryUrl}/${params.treeId}`
  return await request(url, config);
}

/**
 * `request()` reports a failure through its errorCallback and still resolves, so a
 * validation rejected by the API resolved as a success: the offline queue took the
 * tree for synced, deleted it and told the user it had been stored, while nothing had
 * been stored server side. The failure is turned into a real rejection here, the same
 * way `createTree` does it.
 */
async function requestOrThrow(url: string, config?: RequestConfig) {
  let requestError: any = null;

  const response = await request(url, {
    ...config,
    errorCallback: (error: any) => {
      requestError = error;
      if (config && config.errorCallback) {
        config.errorCallback(error);
      }
    }
  });

  if (requestError) {
    throw requestError;
  }

  return response;
}

export async function validateTree(treeId: string, config?: RequestConfig) {
  const url = `${ConfigurationService.urls().treeValidationUrl}/${treeId}/validation`
  return await requestOrThrow(url, config);
}

export async function certifyTree(treeId: string, config?: RequestConfig) {
  const url = `${ConfigurationService.urls().treeCertifyUrl}/${treeId}`
  return await requestOrThrow(url, config);
}

export function normalize(data: any) {
  const treeData = {
    id: integerOrNull(data.id),
    contractId: integerOrNull(data.contractId),
    description: stringOrNull(data.description),
    numberPlanted: integerOrNull(data.numberPlanted),
    lat: floatOrNull(data.lat),
    lng: floatOrNull(data.lng),
    altitude: floatOrNull(data.altitude),
    accuracy: floatOrNull(data.accuracy),
    altitude_accuracy: floatOrNull(data.altitude_accuracy),
    // orientation: floatOrNull(data.orientation),
    validationHash: stringOrNull(data.validationHash),
    plantedHash: stringOrNull(data.plantedHash),
    fruit_tree: integerOrNull(data.fruit_tree),
    user: dataOrNull(data.user),
    contract: dataOrNull(data.contract),
    treeType: dataOrNull(data.treeType),
    photos: dataOrNull(data.photos),
    treeValidations: dataOrNull(data.treeValidations),
    createdAt: stringOrNull(data.createdAt),
    updatedAt: stringOrNull(data.updatedAt),
  };

  treeData["photos"] = toObject(treeData.photos);
  treeData["treeType"] = toObject(treeData.treeType);
  treeData["user"] = toObject(treeData.user);

  if (Array.isArray(treeData.treeValidations)) {
    treeData.treeValidations = treeData.treeValidations.map((v: any) => ({
      ...v,
      accuracy: floatOrNull(v.accuracy || v.gps_accuracy),
      altitude: floatOrNull(v.altitude),
      altitude_accuracy: floatOrNull(v.altitude_accuracy || v.altitudeAccuracy),
    }));
  }

  return treeData;
}

function formatTreeData(data: any) {
  // If data is already an object (not FormData), return it as-is
  if (typeof data === 'object' && !(data instanceof FormData)) {
    return data;
  }
  // For FormData or other iterables, convert to object
  const item: any = Object.fromEntries(data);
  // We no longer create a Blob URL here because it leaks memory if not revoked.
  // The UI components should handle creating/revoking URLs for Blobs themselves.
  return item;
}

export async function downloadKML(setShowLoader: (show: boolean) => void, filter?: any) {
  setShowLoader(true);

  try {
    let url = `${ConfigurationService.urls().downloadkml}`;

    // Debug: Log the filter structure
    console.log('downloadKML - filter received:', filter);

    // Add filter parameter as JSON string if filter exists and has values
    if (filter && hasFilterValues(filter)) {
      const filterString = JSON.stringify(filter);
      url += `?filter=${encodeURIComponent(filterString)}`;
      console.log('Final KML download URL:', url);
    } else {
      console.log('No filter values provided, using base URL');
    }

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${getToken()}`
      }
    });

    if (response.ok) {
      const kmlData = await response.text();
      console.log('KML data received, size:', kmlData.length, 'characters');
      const blob = new Blob([kmlData], { type: 'application/vnd.google-earth.kml+xml' });
      const blobURL = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobURL;
      link.setAttribute('download', 'trees.kml');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobURL);
      console.log('KML download completed successfully');
    } else {
      const errorText = await response.text();
      console.error('KML download failed with status:', response.status, response.statusText);
      console.error('Error response:', errorText);
      throw new Error(`Failed to download KML: ${response.status} ${response.statusText}`);
    }
  } catch (error) {
    console.error('Error downloading KML:', error);
    message.error('Error downloading KML file');
  } finally {
    setShowLoader(false);
  }
}

// Helper function to check if filter has meaningful values
function hasFilterValues(filter: any): boolean {
  if (!filter) return false;

  // Check if any meaningful filter values exist
  return !!(
    filter.contractId ||
    filter.treeId ||
    filter.dateStart ||
    filter.dateEnd ||
    filter.sortField ||
    filter.sortOrder
  );
}
